/**
 * Savings badge format test.
 *
 * Every computed dollar badge ([data-savings-badge="amount"]) is rendered with a % copy
 * ([data-savings-badge="percent"]) as its next sibling. Product form scripts rewrite and
 * toggle the dollar badges on variant changes without knowing about the % copy, so this
 * mirrors those changes onto it:
 * - visibility (`hidden` class / `hidden` attribute / inline display) is always copied
 * - text is recalculated for [data-savings-live] copies, swapping the `$X` for `N%`
 *   measured against the copy's own [data-savings-price] (price paid) or [data-savings-total]
 *   (price before savings), or the [data-savings-price] of the element matched by
 *   [data-savings-price-from="<selector>"], else the current [data-main-product-price]
 *   (copy without a `$X` is mirrored as-is; [data-savings-html] copies keep the markup)
 * - [data-savings-follow="<selector>"] copies take their savings from the first matching
 *   non-zero dollar badge instead (e.g. a countdown banner following the PDP's live save pill)
 *
 * Which copy is visible is controlled by <html data-savings-format="percent"> (see theme.liquid).
 */
;(() => {
  const AMOUNT = '[data-savings-badge="amount"]'
  const PERCENT = '[data-savings-badge="percent"]'
  const MONEY = /\$([\d,]+(?:\.\d{1,2})?)/

  const parseCents = (text, pattern = MONEY) => {
    const match = text?.match(pattern)
    return match ? Math.round(parseFloat(match[1].replace(/,/g, '')) * 100) : null
  }

  // Merchant-entered prices are sometimes saved without the `$` (e.g. "2198").
  const PRICE = /\$?([\d,]+(?:\.\d{1,2})?)/

  // Keep in sync with snippets/savings-percent.liquid
  const percentOff = (savings, price) => {
    const base = price + Math.max(0, savings)
    if (base <= 0) return 0
    // Trim float noise first so a true .5 computed as .4999999 still rounds up.
    return Math.round(Number(((Math.max(0, savings) * 100) / base).toFixed(4)))
  }

  const getPercentBadge = ($amount) => {
    const $next = $amount.nextElementSibling
    return $next?.matches(PERCENT) ? $next : null
  }

  const getFollowedSavings = ($percent) => {
    const selector = $percent.dataset.savingsFollow
    if (!selector) return null
    for (const $source of document.querySelectorAll(selector)) {
      if ($source.matches(PERCENT)) continue
      const savings = parseCents($source.textContent)
      if (savings) return savings
    }
    return null
  }

  const syncVisibility = ($amount, $percent) => {
    $percent.classList.toggle('hidden', $amount.classList.contains('hidden'))
    if ($percent.hidden !== $amount.hidden) $percent.hidden = $amount.hidden
    if ($percent.style.display !== $amount.style.display) $percent.style.display = $amount.style.display
  }

  const syncText = ($amount, $percent) => {
    if (!$percent.hasAttribute('data-savings-live')) return
    const html = $percent.hasAttribute('data-savings-html')
    const text = $amount.textContent.trim()
    const content = html ? $amount.innerHTML : text
    const write = (value) => {
      if (html && $percent.innerHTML !== value) $percent.innerHTML = value
      else if (!html && $percent.textContent !== value) $percent.textContent = value
    }
    const savings = getFollowedSavings($percent) ?? parseCents(text)
    const total = parseCents($percent.dataset.savingsTotal, PRICE)
    const priceFrom = $percent.dataset.savingsPriceFrom
    const priceText =
      (priceFrom && document.querySelector(priceFrom)?.dataset.savingsPrice) ??
      $percent.dataset.savingsPrice ??
      document.querySelector('[data-main-product-price]')?.textContent
    const price = total != null && savings != null ? total - savings : parseCents(priceText, PRICE)
    if (savings == null || price == null) {
      write(content)
      return
    }
    write(content.replace(MONEY, `${percentOff(savings, price)}%`))
  }

  const syncBadge = ($amount) => {
    const $percent = getPercentBadge($amount)
    if (!$percent) return
    syncVisibility($amount, $percent)
    syncText($amount, $percent)
  }

  // The hybrid cart shows every line's "Save N%" from one global setting (switched to percent
  // in hybrid-cart-main.liquid), but bundle-discounted lines keep their dollar savings, while
  // upsell cards always render "Save $X" and need the %. Its markup is compiled, so elements
  // are matched on discount tags, hidden price labels and the upsell card class. The cart
  // renders inside <hybrid-cart>'s shadow root, out of reach of document queries/observers.
  const BUNDLE_DISCOUNT = /bundl/i
  // Discount tag pills only; product titles share the line-clamp class and can contain "Bundle".
  const DISCOUNT_TAG = 'div.w-max > span.line-clamp-2'
  const PERCENT_TEXT = /\d+(\.\d+)?%/
  const UPSELL_CARD = '.upsell-card-item'
  const formatMoney = (cents) =>
    `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 })}`
  const findText = ($el, pattern) =>
    [...$el.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && pattern.test(node.data))
  const totalValueOf = ($scope, owns) =>
    [...$scope.querySelectorAll('span')].find(($el) => owns($el) && $el.textContent.trim().startsWith('Total Value $'))

  const syncCartBundleSavings = ($root) => {
    $root.querySelectorAll('li span.sr-only').forEach(($label) => {
      if ($label.textContent.trim() !== 'Discount') return
      const $text = findText($label.parentElement, PERCENT_TEXT)
      const $line = $label.closest('li')
      if (!$text || !$line) return
      // Child items and upsell cards render inside their parent line's <li>.
      const owns = ($el) => $el.closest('li') === $line && !$el.closest(UPSELL_CARD)
      const own = (selector) => [...$line.querySelectorAll(selector)].filter(owns)
      if (!own(DISCOUNT_TAG).some(($tag) => BUNDLE_DISCOUNT.test($tag.textContent))) return

      const $sale = own('span.sr-only').find(($el) => $el.textContent.trim() === 'Sale price')
      const price = parseCents($sale?.nextSibling?.textContent)
      const compareAt = parseCents((own('del')[0] ?? totalValueOf($line, owns))?.textContent)
      if (price == null || compareAt == null || compareAt <= price) return
      $text.data = $text.data.replace(PERCENT_TEXT, () => formatMoney(compareAt - price))
    })
  }

  const syncCartUpsellSavings = ($root) => {
    $root.querySelectorAll(`${UPSELL_CARD} span`).forEach(($badge) => {
      if (!$badge.textContent.trim().startsWith('Save ')) return
      const $text = findText($badge, MONEY)
      if (!$text) return
      const $card = $badge.closest(UPSELL_CARD)
      const total = parseCents((totalValueOf($card, () => true) ?? $card.querySelector('del'))?.textContent)
      const savings = parseCents($text.data)
      if (total == null || savings == null || total <= savings) return
      $text.data = $text.data.replace(MONEY, () => `${percentOff(savings, total - savings)}%`)
    })
  }

  let $cartRoot = null
  const syncCart = () => {
    if (!$cartRoot || document.documentElement.dataset.savingsFormat !== 'percent') return
    syncCartBundleSavings($cartRoot)
    syncCartUpsellSavings($cartRoot)
  }
  const observeCart = () => {
    const $root = document.querySelector('hybrid-cart')?.shadowRoot
    if (!$root || $root === $cartRoot) return
    $cartRoot = $root
    new MutationObserver(syncCart).observe($root, { subtree: true, childList: true, characterData: true })
    syncCart()
  }
  customElements.whenDefined('hybrid-cart').then(() => setTimeout(observeCart))

  // Liquid renders the initial % text where it can calculate it; merchant-entered promo
  // copy (e.g. "SAVE $600") renders an empty % copy that is filled in here.
  document.querySelectorAll(AMOUNT).forEach(($amount) => {
    const $percent = getPercentBadge($amount)
    if (!$percent) return
    syncVisibility($amount, $percent)
    if (!$percent.textContent.trim()) syncText($amount, $percent)
  })
  observeCart()

  new MutationObserver((mutations) => {
    const $$changed = new Set()
    mutations.forEach(({ type, target }) => {
      if (type === 'attributes') {
        if (target.matches(AMOUNT)) $$changed.add(target)
        return
      }
      const $amount = (target.nodeType === Node.ELEMENT_NODE ? target : target.parentElement)?.closest(AMOUNT)
      if ($amount) $$changed.add($amount)
    })
    document.querySelectorAll('[data-savings-follow]').forEach(($percent) => {
      const $amount = $percent.previousElementSibling
      if ($amount?.matches(AMOUNT)) $$changed.add($amount)
    })
    $$changed.forEach(syncBadge)
    observeCart()
  }).observe(document.documentElement, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden'],
  })
})()
